import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Building2, Mail, Lock } from "lucide-react";
import { API, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

export default function Login() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const { setUser } = useAuth();
  const navigate = useNavigate();

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const { data } = await API.post("/auth/login", { email, password });
      setUser(data);
      navigate("/");
    } catch (err) {
      setError(formatApiError(err, "Credenziali non valide"));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-slate-50">
      <div className="flex items-center justify-center p-6">
        <div className="w-full max-w-md fade-in">
          <div className="flex items-center gap-3 mb-10">
            <div className="h-12 w-12 rounded-xl bg-blue-800 flex items-center justify-center">
              <Building2 className="h-6 w-6 text-white" />
            </div>
            <div>
              <h1 className="font-heading text-2xl font-extrabold tracking-tight text-slate-900">CLGGEST</h1>
              <p className="font-mono text-[10px] uppercase tracking-widest text-amber-600">Gestione Pratiche Edilizie</p>
            </div>
          </div>
          <h2 className="font-heading text-xl font-bold text-slate-900 mb-1">Accedi allo studio</h2>
          <p className="text-sm text-slate-500 mb-8">Area riservata a titolare e collaboratori.</p>

          <form onSubmit={submit} className="space-y-5" data-testid="login-form">
            <div className="space-y-2">
              <Label htmlFor="email">Email</Label>
              <div className="relative">
                <Mail className="absolute left-3 top-3 h-4 w-4 text-slate-400" />
                <Input
                  id="email"
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="pl-9"
                  placeholder="nome@studio.it"
                  data-testid="login-email-input"
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="password">Password</Label>
              <div className="relative">
                <Lock className="absolute left-3 top-3 h-4 w-4 text-slate-400" />
                <Input
                  id="password"
                  type="password"
                  required
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="pl-9"
                  placeholder="••••••••"
                  data-testid="login-password-input"
                />
              </div>
            </div>
            {error && (
              <p className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2" data-testid="login-error">
                {error}
              </p>
            )}
            <Button type="submit" disabled={loading} className="w-full bg-blue-800 hover:bg-blue-900" data-testid="login-submit-button">
              {loading ? "Accesso in corso..." : "Accedi"}
            </Button>
          </form>
          <div className="mt-5 text-center">
            <Link to="/forgot-password" className="text-sm text-blue-800 hover:underline" data-testid="forgot-password-link">
              Password dimenticata?
            </Link>
          </div>
        </div>
      </div>
      <div className="hidden lg:block relative">
        <img
          src="https://images.unsplash.com/photo-1503387762-592deb58ef4e?crop=entropy&cs=srgb&fm=jpg&q=85"
          alt="Studio tecnico"
          className="absolute inset-0 h-full w-full object-cover"
        />
        <div className="absolute inset-0 bg-slate-900/70" />
        <div className="absolute bottom-0 left-0 p-12">
          <p className="font-mono text-xs uppercase tracking-widest text-amber-500 mb-3">Archivio Digitale</p>
          <h3 className="font-heading text-3xl font-extrabold text-white leading-tight max-w-md">
            CILA, SCIA, Permessi di Costruire e pratiche catastali. Tutto in un unico posto.
          </h3>
        </div>
      </div>
    </div>
  );
}
